import React, { useState, useEffect, useRef, useReducer } from "react";

import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import OSSFullPageSpinner from "../OSSFullPageSpinner/OSSFullPageSpinner";
import auth from "../services/auth";
import { redirectToSSO, currentlyOnSsoPortal } from "../utils/user";

const AuthContext = React.createContext();

const axiosConfig = {
  method: "get",
  url: "users/self/user-data",
  withCredentials: true,
  timeout: 5000
};
const onSsoPortal = currentlyOnSsoPortal();
const dataFetchReducer = (state, action) => {
  switch (action.type) {
    case "FETCH_INIT":
      return { ...state, isLoading: true, isError: false };
    case "FETCH_SUCCESS":
      return {
        ...state,
        isLoading: false,
        isError: false,
        data: action.payload
      };
    case "FETCH_FAILURE":
      return {
        ...state,
        isLoading: false,
        isError: true,
        errorData: action.payload
      };
    default:
      throw new Error();
  }
};

/**
 * If logged in user has the given permissions, return true.
 *
 * @param {[string]} permissions The list of permissions to check for
 * @param {boolean} requireAll If true, user must have all permissions in the list
 */
function userHasPermissions(permissions = [], requireAll = false) {
  const userPermissions = this.actions;

  if (!Array.isArray(permissions)) {
    permissions = [permissions];
  }

  if (permissions.length === 0) {
    return true;
  }

  const hasAllPermissons = permissions.every(permission =>
    userPermissions.includes(permission)
  );

  const hasAtLeastOnePermission = userPermissions.some(permission =>
    permissions.includes(permission)
  );

  return requireAll ? hasAllPermissons : hasAtLeastOnePermission;
}

/**
 * The AuthProvider will call the 'whoami' endpoint, and provide the
 * resulting user object in the AuthContext. The AuthProvider is designed
 * to wrap the root component of an application. It is not concerned
 * about the implimentation details of application's user management,
 * ie. redux vs context vs local state. It only provides the user object,
 * and will redirect to the SSO login if whoami fails.
 * @param {*} props
 */
function AuthProvider(props) {
  //an array of network retry attempts, with the delay inbetween attemps in milliseconds
  const networkRetrys = [2000, 3000, 6000, 10000];
  const sleep = delay => new Promise(resolve => setTimeout(resolve, delay));

  const networkAttempts = useRef(0);
  const fetchInitiated = useRef(false);
  const [loadingMsg, setLoadingMsg] = useState("");

  const [state, dispatch] = useReducer(dataFetchReducer, {
    isLoading: false,
    isError: false,
    data: false,
    errorData: false
  });

  const { data, isLoading, isError, errorData } = state;

  //initial call to user_data
  useEffect(() => {
    let didCancel = false;

    const fetchData = async () => {
      dispatch({ type: "FETCH_INIT" });

      try {
        const result = await auth(axiosConfig);
        if (!didCancel) {
          dispatch({ type: "FETCH_SUCCESS", payload: result.data });
        }
      } catch (error) {
        if (!didCancel) {
          dispatch({ type: "FETCH_FAILURE", payload: error });
        }
      }
    };

    fetchData();

    return () => {
      didCancel = true;
    };
  }, []);

  useEffect(() => {
    const retryRequest = async delay => {
      errorData.message &&
        setLoadingMsg(`Connection error: retry in ${delay / 1000} second(s)`);

      await sleep(delay);
      dispatch({ type: "FETCH_INIT" });
      setLoadingMsg(`Connecting...`);
      try {
        const result = await auth(axiosConfig);
        dispatch({ type: "FETCH_SUCCESS", payload: result.data });
      } catch (error) {
        networkAttempts.current = networkAttempts.current + 1;
        dispatch({ type: "FETCH_FAILURE", payload: error });
        fetchInitiated.current = false;
      }
    };

    //if the previous api call had an error, but got no server response (network issue)
    //retry the api call with a delay from the networkRetry's current index
    if (
      isError &&
      !errorData.response &&
      !isLoading &&
      !fetchInitiated.current &&
      networkRetrys[networkAttempts.current]
    ) {
      fetchInitiated.current = true;
      retryRequest(networkRetrys[networkAttempts.current]);
    }

    //if all network retrys have failed, redirect to sso login
    if (networkRetrys.length === networkAttempts.current) {
      onSsoPortal
        ? setLoadingMsg(`Connection failed, try refreshing later`)
        : redirectToSSO();
    }
  }, [
    isLoading,
    setLoadingMsg,
    isError,
    errorData,
    networkRetrys,
    fetchInitiated
  ]);

  const renderNoAccess = () => {
    return (
      <Grid container spacing={6}>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column"
          }}
        >
          <OSSFullPageSpinner />
          <p>There has been an error with your account setup.</p>
          {data.is_staff ? (
            <p>
              Please submit a ticket to the{" "}
              <a href="https://openslate.freshservice.com/support/tickets/new">
                OpenSlate helpdesk
              </a>{" "}
              for assistance
            </p>
          ) : (
            <p>
              Please contact your DoubleVerify Sales representative for assistance.
            </p>
          )}
        </Grid>
      </Grid>
    );
  };

  /* if we got a server response, then we can handle the status code
     404 - user not found, need to log in
     403 - user not authorized for the application they are asking for
     429 - user has too many concurrent sessions */
  if (isError) {
    if (errorData.response) {
      const { status } = errorData.response;

      switch (status) {
        case 404:
          /* the portal apps app has both authenticated and unauthenticated views
         so it is the only app that is allowed to render without a user
        object returned by user_data */
          if (onSsoPortal)
            return <AuthContext.Provider value={false} {...props} />;

          //if not on the portal and user_data failed, do a redirect to login
          redirectToSSO();
          break;
        case 403:
          //if you get a 403 on the portal, don't redirect - it will case a redirect loop
          if (onSsoPortal) return renderNoAccess();
          redirectToSSO({ msg: "UNAUTHORIZED_APPLICATION" });
          break;
        case 429:
          if (onSsoPortal) {
            window.history.pushState({}, "", `?msg=CONCURRENT_SESSIONS`);
            return <AuthContext.Provider value={false} {...props} />;
          } else {
            redirectToSSO({ msg: "CONCURRENT_SESSIONS" });
          }
          break;

        default:
          if (onSsoPortal) {
            window.history.pushState({}, "", `?msg=AUTH_ERROR`);
            return <AuthContext.Provider value={false} {...props} />;
          } else {
            redirectToSSO();
          }

          break;
      }
    } else {
      if (onSsoPortal) {
        window.history.pushState({}, "", `?msg=AUTH_ERROR`);
        return <AuthContext.Provider value={false} {...props} />;
      } else {
        redirectToSSO();
      }
    }
  }

  //successful user_data call, returned a user object
  if (!isLoading && !isError && data) {
    //the user needs at least one app in their app list,
    //and have the portal app
    if (
      !data.apps ||
      !data.apps.find(app => app.name.toLowerCase().includes("portal"))
    ) {
      return renderNoAccess();
    }

    //function to check if a user has an action
    data.hasPermissions = userHasPermissions;

    data.profileId = data?.profile?.id || null;
    data.company = data.is_staff
      ? "OpenSlate"
      : data?.profile?.subscription
        ? data.profile.subscription.name
        : "unknown";

    //if the user is not staff (subscription user) and only has the
    //portal app, it means they are expired, show msg
    if (!data.is_staff && data.apps.length === 1) {
      window.history.pushState({}, "", `?msg=EXPIRED_SUBSCRIPTION`);
    }

    return <AuthContext.Provider value={data} {...props} />;
  }

  return (
    <Grid
      container
      spacing={6}
      direction="row"
      justifyContent="center"
      alignItems="center"
    >
      <Grid
        item
        xs={12}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          direction: "row",
          background: "white",
          minHeight: "69px",
          borderBottom: "1px solid #dfe4ea"
        }}
      >
        <Typography>{loadingMsg}</Typography>
      </Grid>
      <Grid item xs={12}>
        <OSSFullPageSpinner />
      </Grid>
    </Grid>
  );
}

function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
}

export { AuthContext, AuthProvider, useAuth };
